import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import ClubCalendar from '../components/ClubCalendar';
import { getClubById, joinClub, leaveClub, deleteClub, transferOwnership, searchUsers, inviteUserToClub } from '../services/api';
import { MapPin, Clock, Calendar as CalendarIcon, User, ArrowLeft, Edit, Trash2, Users as UsersIcon, ChevronDown, ChevronUp, TrendingDown, Crown, Trophy, Lock, Mail, UserPlus, X, Search, Award, XCircle, TrendingUp, CheckCircle, Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ClubFeed from '../components/ClubFeed';
import { DotLoader } from 'react-spinners';
import defaultAvatar from '../assets/default-avatar.jpg'
import Footer from '../components/Footer';
import FooterPWA from '../components/FooterPWA';
import { Swords, Target } from 'lucide-react';
import CreateClubWar from '../components/CreateClubWar';
import api from '../services/api';

function ClubPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTransferOwnershipModal, setShowTransferOwnershipModal] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState(null);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [showWarModal, setShowWarModal] = useState(false);
  const [clubWars, setClubWars] = useState([]);
  const [warsLoading, setWarsLoading] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  
  // États pour le modal d'invitation
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [inviting, setInviting] = useState(false);

  const [activeTab, setActiveTab] = useState('wars');

  useEffect(() => {
    fetchClub();
  }, [id]);

  // Ajouter un useEffect séparé pour les guerres
  useEffect(() => {
    if (club) {
      fetchClubWars();
    }
  }, [club]);

  const fetchClub = async () => {
    try {
      const data = await getClubById(id);
      setClub(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement du club:', error);
      setLoading(false);
    }
  };

  const fetchClubWars = async () => {
    if (!club?._id) return;
    
    setWarsLoading(true);
    try {
      const response = await api.get(`/club-wars/club/${club._id}`);
      console.log('📦 Données reçues:', response.data);
      
      // Filtrer pour avoir seulement les guerres pending et active
      const filteredWars = (response.data.wars || []).filter(w => ['pending', 'active'].includes(w.statut));
      console.log('✅ Guerres filtrées:', filteredWars);
      setClubWars(filteredWars);
    } catch (error) {
      console.error('Erreur chargement guerres:', error);
    }
    setWarsLoading(false);
  };

  // Rechercher des utilisateurs pour les inviter
  const handleSearchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const data = await searchUsers(query);
      
      // Filtrer les utilisateurs déjà membres ou déjà invités
      const filtered = data.filter(u => 
        !club.membres?.some(m => (m._id || m) === u._id) &&
        !club.invitations?.some(inv => inv.user === u._id && inv.status === 'pending')
      );
      
      setSearchResults(filtered);
    } catch (error) {
      console.error('Erreur recherche:', error);
      setSearchResults([]);
    }
    setSearchLoading(false);
  };

  const handleAcceptWar = async (warId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir accepter ce défi ?')) return;
    
    try {
      await api.post(`/club-wars/${warId}/accept`);
      alert('✅ Défi accepté ! Que la bataille commence !');
      fetchClubWars(); // Rafraîchir les guerres
      fetchClub(); // Rafraîchir les infos du club
    } catch (error) {
      console.error('Erreur acceptation:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'acceptation');
    }
  };

  const handleRejectWar = async (warId) => {
    const reason = prompt('Raison du refus (optionnel):');
    if (reason === null) return; // Utilisateur a annulé
    
    try {
      await api.post(`/club-wars/${warId}/reject`, { reason });
      alert('❌ Défi refusé');
      fetchClubWars(); // Rafraîchir les guerres
    } catch (error) {
      console.error('Erreur refus:', error);
      alert(error.response?.data?.message || 'Erreur lors du refus');
    }
  };

  // Inviter un utilisateur
  const handleInviteUser = async (userId) => {
    setInviting(true);
    try {
      await inviteUserToClub(club._id, userId);
      
      alert('✅ Invitation envoyée avec succès !');
      setSearchQuery('');
      setSearchResults([]);
      fetchClub(); // Rafraîchir pour voir les invitations
    } catch (error) {
      console.error('Erreur invitation:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'invitation');
    }
    setInviting(false);
  };

  const handleJoin = async () => {
    if (!isAuthenticated) {
      alert('Vous devez être connecté pour rejoindre un club');
      navigate('/login');
      return;
    }

    try {
      await joinClub(id);
      fetchClub();
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'inscription');
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedNewOwner) return;
    
    try {
      await transferOwnership(club._id, selectedNewOwner);
      await leaveClub(club._id);
      navigate('/');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du transfert de propriété');
    }
  };

  const handleLeave = async () => {
    const isOwner = club.createur._id === user._id;
    const membersCount = club.membres.length;
  
    if (isOwner) {
      if (membersCount === 1) {
        const confirm = window.confirm(
          'Vous êtes le seul membre. Quitter le club le supprimera définitivement. Êtes-vous sûr ?'
        );
        if (!confirm) return;
        
        try {
          await deleteClub(club._id);
          navigate('/');
        } catch (error) {
          console.error('Erreur:', error);
          alert('Erreur lors de la suppression du club');
        }
      } else {
        setShowTransferOwnershipModal(true);
      }
    } else {
      try {
        await leaveClub(club._id);
        navigate('/');
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur lors de la sortie du club');
      }
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer ce club ? Cette action est irréversible.');
    if (!confirmed) return;

    try {
      await deleteClub(id);
      navigate('/my-created-clubs');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const isMember = () => {
    if (!isAuthenticated || !user || !club || !club.membres) return false;
    return club.membres.some(membre => {
      if (typeof membre === 'object' && membre._id) {
        return membre._id === user._id;
      }
      return membre === user._id;
    });
  };

  const isCreator = () => {
    if (!isAuthenticated || !user || !club) return false;
    const createurId = typeof club.createur === 'object' ? club.createur._id : club.createur;
    return createurId === user._id;
  };

  const canEdit = () => {
    return isCreator() || (user && user.role === 'admin');
  };

  // Vérifier si l'utilisateur est invité
  const isInvited = () => {
    if (!isAuthenticated || !user || !club) return false;
    return club.invitations?.some(
      inv => inv.user === user._id && inv.status === 'pending'
    );
  };

  // Déterminer si l'utilisateur peut rejoindre
  const canJoin = () => {
    if (!club.isPrivate) return true;
    return isInvited();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <p className="text-xl text-gray-400">Club introuvable</p>
        </div>
      </div>
    );
  }

  const nombreMembres = club.nombreMembres || club.membres?.length || 0;
  const membersToShow = showAllMembers ? club.membres : club.membres?.slice(0, 8);
  const isPrivate = club.isPrivate;

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
  {/* Bouton retour */}
  <button
    onClick={() => navigate('/clubs-list')}
    className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-4 sm:mb-6 bg-slate-900 border border-slate-800 hover:border-indigo-500 px-3 py-2 rounded-lg transition"
  >
    <ArrowLeft size={20} className="mr-2" />
    <span>Retour</span>
  </button>

  <div className={`bg-slate-900 border rounded-xl shadow-lg overflow-hidden mb-4 sm:mb-8 ${
    isPrivate ? 'border-indigo-500/50' : 'border-slate-800'
  }`}>
    {/* Image du club en haut */}
    {club.imageClub ? (
      <div className="w-full h-48 sm:h-64 md:h-80 relative">
        <img
          src={club.imageClub}
          alt={club.nom}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        
        {/* Badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          {isPrivate && (
            <span className="bg-indigo-500/90 backdrop-blur-sm border border-indigo-400 text-slate-900 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-bold shadow-lg flex items-center justify-center gap-1">
              <Lock size={16} />
              <span>Privé</span>
            </span>
          )}
          <span className="bg-slate-900/90 backdrop-blur-sm border border-slate-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-bold shadow-lg">
            {club.niveau}
          </span>
        </div>

        {/* Titre sur l'image (mobile) */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:hidden">
          <h1 className="text-2xl font-bold text-white drop-shadow-lg">
            {club.nom}
          </h1>
          {club.createur && (
            <p className="text-sm text-white/90 mt-1 flex items-center gap-1">
              <Crown size={14} className="text-indigo-400" />
              Créé par {typeof club.createur === 'object' 
                ? `${club.createur.prenom} ${club.createur.nom}`
                : 'Créateur inconnu'}
            </p>
          )}
        </div>
      </div>
    ) : (
      <div className={`w-full h-48 sm:h-64 md:h-80 bg-gradient-to-br flex items-center justify-center relative ${
        isPrivate ? 'from-indigo-900/20 to-slate-950' : 'from-slate-800 to-slate-950'
      }`}>
        {isPrivate ? (
          <Lock size={64} className="text-indigo-500/50 sm:w-24 sm:h-24" />
        ) : (
          <UsersIcon size={64} className="text-slate-700 sm:w-24 sm:h-24" />
        )}
        
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          {isPrivate && (
            <span className="bg-indigo-500/90 backdrop-blur-sm border border-indigo-400 text-slate-900 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-bold shadow-lg flex items-center gap-1">
              <Lock size={16} />
              <span>Privé</span>
            </span>
          )}
          <span className="bg-slate-900/90 backdrop-blur-sm border border-slate-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-bold shadow-lg">
            {club.niveau}
          </span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:hidden">
          <h1 className="text-2xl font-bold text-white drop-shadow-lg">
            {club.nom}
          </h1>
          {club.createur && (
            <p className="text-sm text-white/90 mt-1 flex items-center gap-1">
              <Crown size={14} className="text-indigo-400" />
              Créé par {typeof club.createur === 'object' 
                ? `${club.createur.prenom} ${club.createur.nom}`
                : 'Créateur inconnu'}
            </p>
          )}
        </div>
      </div>
    )}

    {/* Informations du club */}
    <div className="p-4 sm:p-6">
      {/* Titre (desktop) */}
      <div className="hidden sm:block mb-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
          {club.nom}
        </h1>
        {club.createur && (
          <p className="text-sm sm:text-base text-gray-300 flex items-center gap-2">
            <Crown size={16} className="text-indigo-400" />
            Créé par {typeof club.createur === 'object' 
              ? `${club.createur.prenom} ${club.createur.nom}`
              : 'Créateur inconnu'}
          </p>
        )}
      </div>

      {/* Description */}
      {club.description && (
        <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6 leading-relaxed">
          {club.description}
        </p>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className={`flex items-center bg-slate-800 border rounded-xl p-3 sm:p-4 transition ${
          isPrivate 
            ? 'border-indigo-500/30 hover:border-indigo-500/50' 
            : 'border-slate-700 hover:border-indigo-500'
        }`}>
          <Clock size={20} className={`mr-2 sm:mr-3 flex-shrink-0 sm:w-6 sm:h-6 ${
            isPrivate ? 'text-indigo-500' : 'text-indigo-500'
          }`} />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-400">Distance</p>
            <p className="text-sm sm:text-base md:text-lg font-bold text-white truncate">{club.distance}</p>
          </div>
        </div>

        <div className={`flex items-center bg-slate-800 border rounded-xl p-3 sm:p-4 transition ${
          isPrivate 
            ? 'border-indigo-500/30 hover:border-indigo-500/50' 
            : 'border-slate-700 hover:border-indigo-500'
        }`}>
          <CalendarIcon size={20} className={`mr-2 sm:mr-3 flex-shrink-0 sm:w-6 sm:h-6 ${
            isPrivate ? 'text-indigo-500' : 'text-indigo-500'
          }`} />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-400">Séances/sem</p>
            <p className="text-sm sm:text-base md:text-lg font-bold text-white">{club.seances?.length || 0}</p>
          </div>
        </div>

        <div className={`flex items-center bg-slate-800 border rounded-xl p-3 sm:p-4 transition ${
          isPrivate 
            ? 'border-indigo-500/30 hover:border-indigo-500/50' 
            : 'border-slate-700 hover:border-indigo-500'
        }`}>
          <User size={20} className={`mr-2 sm:mr-3 flex-shrink-0 sm:w-6 sm:h-6 ${
            isPrivate ? 'text-indigo-500' : 'text-indigo-500'
          }`} />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-400">Membres</p>
            <p className="text-sm sm:text-base md:text-lg font-bold text-white">{nombreMembres}</p>
          </div>
        </div>
        
        <div className={`flex items-center bg-slate-800 border rounded-xl p-3 sm:p-4 transition ${
          isPrivate 
            ? 'border-indigo-500/30 hover:border-indigo-500/50' 
            : 'border-slate-700 hover:border-indigo-500'
        }`}>
          <MapPin size={20} className={`mr-2 sm:mr-3 flex-shrink-0 sm:w-6 sm:h-6 ${
            isPrivate ? 'text-indigo-500' : 'text-indigo-500'
          }`} />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-400">Ville</p>
            <p className="text-sm sm:text-base md:text-lg font-bold text-white truncate" title={`${club.ville}, ${club.province}`}>
              {club.ville}, {club.province}
            </p>
          </div>
        </div>
      </div>

      {/* Menu déroulant pour les autres actions */}
      <div className="relative mb-4">
        <button
          onClick={() => setShowActionsDropdown(!showActionsDropdown)}
          className="w-full py-3 rounded-xl transition-all font-semibold text-base flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-slate-600"
        >
          <span>Plus d'actions</span>
          {showActionsDropdown ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {/* Dropdown menu */}
        {showActionsDropdown && (
          <div className="mt-2 space-y-2 bg-slate-800 border border-slate-700 rounded-xl p-2 shadow-lg">
            {isAuthenticated && (
              <>
                {isMember() ? (
                  <button
                    onClick={handleLeave}
                    className="w-full py-2.5 rounded-lg transition-all font-semibold text-sm flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white"
                  >
                    <UsersIcon size={18} />
                    Quitter ce groupe
                  </button>
                ) : canJoin() ? (
                  <button
                    onClick={handleJoin}
                    className={`w-full py-2.5 rounded-lg transition-all font-semibold text-sm flex items-center justify-center gap-2 ${
                      isPrivate
                        ? 'bg-indigo-500 hover:bg-indigo-600 text-slate-900'
                        : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                    }`}
                  >
                    <UsersIcon size={18} />
                    {isInvited() ? 'Accepter l\'invitation' : 'Rejoindre ce groupe'}
                  </button>
                ) : (
                  <div className="w-full py-2.5 rounded-lg bg-slate-900 border border-indigo-500/50 text-indigo-500 font-semibold text-sm flex items-center justify-center gap-2">
                    <Lock size={16} />
                    <span>Groupe privé - Invitation requise</span>
                  </div>
                )}
              </>
            )}

            {/* Bouton inviter pour le créateur si club privé */}
            {isCreator() && isPrivate && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="w-full py-2.5 rounded-lg transition-all font-semibold text-sm flex items-center justify-center gap-2 bg-indigo-500 text-slate-900 hover:bg-indigo-600"
              >
                <UserPlus size={18} />
                Inviter des membres
              </button>
            )}

            {/* Boutons créateur */}
            {canEdit() && (
              <>
                <button
                  onClick={() => navigate(`/edit-club/${club._id}`)}
                  className="w-full py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2 border border-slate-600 hover:border-indigo-500 text-sm"
                >
                  <Edit size={18} />
                  Modifier
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full py-2.5 bg-red-600/20 hover:bg-red-600 text-red-500 hover:text-white rounded-lg font-semibold transition flex items-center justify-center gap-2 border border-red-500/50 hover:border-red-500 text-sm"
                >
                  <Trash2 size={18} />
                  Supprimer
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Système d'onglets avec 4 onglets */}
      {isMember() && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg overflow-hidden">
          {/* Header avec onglets */}
          <div className="border-b border-slate-800">
            <div className="flex overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveTab('wars')}
                className={`flex-1 min-w-[100px] px-3 sm:px-4 py-4 font-semibold text-xs sm:text-base transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'wars'
                    ? 'text-white bg-slate-800 border-b-2 border-indigo-500'
                    : 'text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Swords size={16} className={`sm:w-[18px] sm:h-[18px] ${activeTab === 'wars' ? 'text-indigo-500' : ''}`} />
                <span className="hidden sm:inline">Guerres & Défis</span>
                <span className="sm:hidden">Guerres</span>
              </button>
              
              <button
                onClick={() => setActiveTab('feed')}
                className={`flex-1 min-w-[100px] px-3 sm:px-4 py-4 font-semibold text-xs sm:text-base transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'feed'
                    ? 'text-white bg-slate-800 border-b-2 border-indigo-500'
                    : 'text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Activity size={16} className={`sm:w-[18px] sm:h-[18px] ${activeTab === 'feed' ? 'text-indigo-500' : ''}`} />
                <span>Feed</span>
              </button>
              
              <button
                onClick={() => setActiveTab('calendar')}
                className={`flex-1 min-w-[100px] px-3 sm:px-4 py-4 font-semibold text-xs sm:text-base transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'calendar'
                    ? 'text-white bg-slate-800 border-b-2 border-indigo-500'
                    : 'text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <CalendarIcon size={16} className={`sm:w-[18px] sm:h-[18px] ${activeTab === 'calendar' ? 'text-indigo-500' : ''}`} />
                <span>Calendrier</span>
              </button>
              
              <button
                onClick={() => setActiveTab('members')}
                className={`flex-1 min-w-[100px] px-3 sm:px-4 py-4 font-semibold text-xs sm:text-base transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'members'
                    ? 'text-white bg-slate-800 border-b-2 border-indigo-500'
                    : 'text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <UsersIcon size={16} className={`sm:w-[18px] sm:h-[18px] ${activeTab === 'members' ? 'text-indigo-500' : ''}`} />
                <span>Membres</span>
              </button>
            </div>
          </div>

          {/* Contenu des onglets */}
          <div className="p-4 sm:p-6">
            {/* Onglet 1: Guerres & Défis */}
            {activeTab === 'wars' && (
              <div className="space-y-4">
                {/* Bouton Challenges */}
                <button
                  onClick={() => navigate(`/club/${club._id}/challenges`)}
                  className="w-full py-3 rounded-xl transition-all font-bold text-base flex items-center justify-center gap-2 shadow-md hover:shadow-lg active:scale-95 bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  <Trophy size={20} />
                  Voir les challenges
                </button>

                {/* ⚔️ SECTION GUERRES DE CLUBS - Toujours visible pour les membres */}
                    {isMember() && (
                    <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-8">
                      {/* Header avec actions */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                          <Swords size={24} className="text-red-500" />
                          Guerres de Clubs
                        </h2>
                        
                        <div className="flex items-center gap-2">
                          {/* Bouton Leaderboard */}
                          <button
                            onClick={() => navigate('/clubs/leaderboard')}
                            className="flex-1 sm:flex-none px-4 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-yellow-500/50 text-yellow-500 rounded-xl transition-all flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-yellow-500/20 group"
                            title="Classement des clubs"
                          >
                            <Crown size={18} className="group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-semibold">Classement</span>
                          </button>
                          
                          {/* Bouton Lancer un défi */}
                          {canEdit() && !clubWars.some(w => ['pending', 'active'].includes(w.statut)) && (
                            <button
                              onClick={() => setShowWarModal(true)}
                              className="flex-1 sm:flex-none px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-600 hover:from-indigo-700 hover:to-indigo-700 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-indigo-500/30"
                            >
                              <Target size={18} />
                              <span className="text-sm">Lancer un défi</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Stats rapides - Grid amélioré */}
                      {club.warStats && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                          <div className="bg-slate-800 border border-slate-700 hover:border-indigo-500/50 rounded-xl p-4 transition-all hover:scale-105 group">
                            <div className="flex items-center justify-between mb-2">
                              <Trophy size={20} className="text-indigo-500 group-hover:scale-110 transition-transform" />
                              <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-500 to-indigo-400 bg-clip-text text-transparent">
                                {club.warStats.points || 0}
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm text-gray-400 font-medium">Points</div>
                          </div>

                          <div className="bg-slate-800 border border-slate-700 hover:border-indigo-500/50 rounded-xl p-4 transition-all hover:scale-105 group">
                            <div className="flex items-center justify-between mb-2">
                              <Award size={20} className="text-indigo-500 group-hover:scale-110 transition-transform" />
                              <div className="text-2xl sm:text-3xl font-bold text-indigo-500">
                                {club.warStats.warsWon || 0}
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm text-gray-400 font-medium">Victoires</div>
                          </div>

                          <div className="bg-slate-800 border border-slate-700 hover:border-indigo-500/50 rounded-xl p-4 transition-all hover:scale-105 group">
                            <div className="flex items-center justify-between mb-2">
                              <XCircle size={20} className="text-indigo-500 group-hover:scale-110 transition-transform" />
                              <div className="text-2xl sm:text-3xl font-bold text-indigo-500">
                                {club.warStats.warsLost || 0}
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm text-gray-400 font-medium">Défaites</div>
                          </div>

                          <div className="bg-slate-800 border border-slate-700 hover:border-indigo-500/50 rounded-xl p-4 transition-all hover:scale-105 group">
                            <div className="flex items-center justify-between mb-2">
                              <TrendingUp size={20} className="text-indigo-500 group-hover:scale-110 transition-transform" />
                              <div className="text-2xl sm:text-3xl font-bold text-indigo-500">
                                {club.warStats.winRate || 0}%
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm text-gray-400 font-medium">Taux</div>
                          </div>
                        </div>
                      )}

                      {/* Liste des guerres */}
                      {warsLoading ? (
                        <div className="flex justify-center py-12">
                          <DotLoader size={50} color="#ef4444" />
                        </div>
                      ) : clubWars.length > 0 ? (
                        <>
                          <div className="space-y-4 mb-4">
                            {clubWars.slice(0, 3).map((war) => {
                              const opponent = war.clubInitiateur._id === club._id ? war.clubDefie : war.clubInitiateur;
                              const isInitiator = war.clubInitiateur._id === club._id;
                              const myScore = isInitiator ? war.scoreActuel?.clubInitiateur : war.scoreActuel?.clubDefie;
                              const opponentScore = isInitiator ? war.scoreActuel?.clubDefie : war.scoreActuel?.clubInitiateur;
                              
                              const statusConfig = {
                                active: { 
                                  icon: '🔥', 
                                  text: 'En cours', 
                                  color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50',
                                  borderColor: 'border-indigo-500/50 hover:border-indigo-500'
                                },
                                pending: { 
                                  icon: '⏳', 
                                  text: 'En attente', 
                                  color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50',
                                  borderColor: 'border-indigo-500/50 hover:border-indigo-500'
                                },
                                completed: { 
                                  icon: '🏁', 
                                  text: 'Terminé', 
                                  color: 'bg-slate-500/20 text-slate-400 border-slate-500/50',
                                  borderColor: 'border-slate-700 hover:border-slate-600'
                                }
                              };
                              
                              const status = statusConfig[war.statut] || statusConfig.pending;

                              return (
                                <div
                                  key={war._id}
                                  onClick={() => navigate(`/club-war/${war._id}`)}
                                  className={`bg-slate-800 border ${status.borderColor} rounded-xl p-4 sm:p-5 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg group`}
                                >
                                  {/* Header de la guerre */}
                                  <div className="flex items-start justify-between gap-3 mb-4">
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-bold text-white text-base sm:text-lg mb-2 truncate group-hover:text-indigo-400 transition-colors">
                                        {war.nom}
                                      </h4>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className={`px-3 py-1 ${status.color} rounded-full border text-xs sm:text-sm font-semibold flex items-center gap-1.5`}>
                                          <span>{status.icon}</span>
                                          <span>{status.text}</span>
                                        </span>
                                        <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-400">
                                          <Swords size={14} className="text-red-400" />
                                          <span className="font-medium">vs</span>
                                          <span className="font-semibold text-white truncate max-w-[150px]">
                                            {opponent.nom}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Points en jeu */}
                                    <div className="relative text-center bg-gradient-to-br from-indigo-500/20 to-purple-600/20 border-2 border-indigo-500/50 rounded-xl px-3 py-2 shrink-0 overflow-hidden group hover:scale-105 transition-transform">
                                      {/* Effet de brillance animé */}
                                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                      
                                      {/* Contenu */}
                                      <div className="relative z-10">
                                        <div className="text-xs text-indigo-300 font-semibold mb-0.5 flex items-center justify-center gap-1">
                                          <Trophy size={12} className="text-indigo-400" />
                                          <span>Points</span>
                                        </div>
                                        <div className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent animate-pulse">
                                          {war.pointsEnJeu?.vainqueur || 0}
                                        </div>
                                      </div>
                                      
                                      {/* Particules brillantes */}
                                      <div className="absolute top-0 right-0 w-2 h-2 bg-indigo-400 rounded-full animate-ping opacity-75"></div>
                                      <div className="absolute bottom-0 left-0 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping opacity-75 animation-delay-300"></div>
                                      
                                    </div>
                                  </div>

                                  {/* Barre de score - Guerre active */}
                                  {/* Barre de score - Guerre active */}
                                  {war.statut === 'active' && war.scoreActuel && (
                                    <div className="space-y-3 bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                                      {/* Header avec scores */}
                                      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1 sm:gap-2">
                                      {/* Notre équipe */}
                                      <div className="flex items-center gap-1 sm:gap-2 justify-end min-w-0">
                                        <div className="text-right min-w-0 flex-1">
                                          <div className="text-[10px] sm:text-xs text-gray-400 font-medium truncate">{club.nom}</div>
                                          <div className="text-base sm:text-lg md:text-xl font-bold text-indigo-400 tabular-nums whitespace-nowrap">
                                            {myScore?.toFixed(1) || 0}
                                            <span className="text-[10px] sm:text-xs text-gray-500 ml-0.5 sm:ml-1">km</span>
                                          </div>
                                        </div>
                                        <div className="relative flex-shrink-0">
                                          <div className="w-3 h-3 sm:w-4 sm:h-4 bg-indigo-500 rounded-full shadow-lg shadow-indigo-500/50 animate-pulse"></div>
                                          <div className="absolute inset-0 w-3 h-3 sm:w-4 sm:h-4 bg-indigo-500 rounded-full animate-ping opacity-75"></div>
                                        </div>
                                      </div>

                                      {/* VS Badge - Toujours centré */}
                                      <div className="flex flex-col items-center gap-0.5 sm:gap-1 px-2 sm:px-3 flex-shrink-0">
                                        <Swords size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                                        <span className="text-[10px] sm:text-xs font-bold text-gray-500 tracking-wider whitespace-nowrap">VS</span>
                                      </div>

                                      {/* Équipe adverse */}
                                      <div className="flex items-center gap-1 sm:gap-2 justify-start min-w-0">
                                        <div className="relative flex-shrink-0">
                                          <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-500 rounded-full shadow-lg shadow-red-500/50 animate-pulse"></div>
                                          <div className="absolute inset-0 w-3 h-3 sm:w-4 sm:h-4 bg-red-500 rounded-full animate-ping opacity-75"></div>
                                        </div>
                                        <div className="text-left min-w-0 flex-1">
                                          <div className="text-[10px] sm:text-xs text-gray-400 font-medium truncate">{opponent.nom}</div>
                                          <div className="text-base sm:text-lg md:text-xl font-bold text-red-400 tabular-nums whitespace-nowrap">
                                            {opponentScore?.toFixed(1) || 0}
                                            <span className="text-[10px] sm:text-xs text-gray-500 ml-0.5 sm:ml-1">km</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                      
                                      {/* Barre de progression avec dégradé */}
                                      <div className="relative">
                                        <div className="relative h-6 sm:h-7 bg-slate-900 rounded-full overflow-hidden border-2 border-slate-700 shadow-inner">
                                          {/* Partie indigo (notre équipe) - Gauche */}
                                          <div
                                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-400 transition-all duration-700 ease-out overflow-hidden"
                                            style={{
                                              width: `${(() => {
                                                const total = myScore + opponentScore;
                                                if (total === 0) return 50;
                                                if (opponentScore === 0) return 100;
                                                if (myScore === 0) return 0;
                                                return Math.min((myScore / total * 100), 100);
                                              })()}%`
                                            }}
                                          >
                                            {/* Effet de brillance animé - Gauche vers Droite */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-shimmer-left"></div>
                                          </div>

                                          {/* Partie rouge (équipe adverse) - Droite */}
                                          <div
                                            className="absolute inset-y-0 right-0 bg-gradient-to-l from-red-600 via-red-500 to-red-400 transition-all duration-700 ease-out overflow-hidden"
                                            style={{
                                              width: `${(() => {
                                                const total = myScore + opponentScore;
                                                if (total === 0) return 50;
                                                if (opponentScore === 0) return 0;
                                                if (myScore === 0) return 100;
                                                return Math.round((opponentScore / total * 100));
                                              })()}%`
                                            }}
                                          >
                                            {/* Effet de brillance animé - Droite vers Gauche */}
                                            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-white/30 to-transparent translate-x-full animate-shimmer-right"></div>
                                          </div>

                                          {/* Ligne de séparation centrale */}
                                          <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-slate-600 transform -translate-x-1/2 z-10"></div>

                                          {/* Indicateur de position */}
                                          <div 
                                            className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 transition-all duration-700 ease-out z-20"
                                            style={{
                                              left: `${(() => {
                                                const total = myScore + opponentScore;
                                                if (total === 0) return 50;
                                                if (opponentScore === 0) return 100;
                                                if (myScore === 0) return 0;
                                                return Math.min((myScore / total * 100), 100);
                                              })()}%`
                                            }}
                                          >
                                            {myScore > opponentScore ? (
                                              <Trophy size={16} className="text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.8)] animate-bounce" />
                                            ) : myScore < opponentScore ? (
                                              <Activity size={16} className="text-red-400 drop-shadow-[0_0_8px_rgba(248,113,113,0.8)]" />
                                            ) : (
                                              <div className="w-2 h-2 bg-white rounded-full shadow-lg"></div>
                                            )}
                                          </div>
                                        </div>

                                        {/* Pourcentages aux extrémités */}
                                        <div className="flex justify-between mt-1 text-xs font-semibold tabular-nums">
                                          <span className="text-indigo-400">
                                            {(() => {
                                              const total = myScore + opponentScore;
                                              if (total === 0) return 50;
                                              if (opponentScore === 0) return 100;
                                              if (myScore === 0) return 0;
                                              return Math.round((myScore / total * 100));
                                            })()}%
                                          </span>
                                          <span className="text-red-400">
                                            {(() => {
                                              const total = myScore + opponentScore;
                                              if (total === 0) return 50;
                                              if (opponentScore === 0) return 0;
                                              if (myScore === 0) return 100;
                                              return Math.round((opponentScore / total * 100));
                                            })()}%
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {/* Statistique d'écart avec animation */}
                                      <div className="text-center">
                                        {myScore > opponentScore ? (
                                          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-1.5">
                                            <TrendingUp size={14} className="text-green-400 animate-pulse" />
                                            <span className="text-sm font-bold text-green-400">
                                              +{(myScore - opponentScore).toFixed(1)} km d'avance
                                            </span>
                                          </div>
                                        ) : myScore < opponentScore ? (
                                          <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-1.5">
                                            <TrendingDown size={14} className="text-red-400 animate-pulse" />
                                            <span className="text-sm font-bold text-red-400">
                                              -{(opponentScore - myScore).toFixed(1)} km de retard
                                            </span>
                                          </div>
                                        ) : (
                                          <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-3 py-1.5">
                                            <Activity size={14} className="text-indigo-400" />
                                            <span className="text-sm font-bold text-indigo-400">Égalité parfaite!</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Actions - Guerre en attente */}
                                  {war.statut === 'pending' && !isInitiator && canEdit() && (
                                    <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAcceptWar(war._id);
                                        }}
                                        className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-green-500/30 flex items-center justify-center gap-2"
                                      >
                                        <CheckCircle size={16} />
                                        <span>Accepter</span>
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRejectWar(war._id);
                                        }}
                                        className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-red-500/30 flex items-center justify-center gap-2"
                                      >
                                        <XCircle size={16} />
                                        <span>Refuser</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Bouton voir l'historique */}
                          {clubWars.length > 3 && (
                            <button
                              onClick={() => navigate(`/club/${club._id}/wars`)}
                              className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-semibold transition-all border border-slate-700 hover:border-red-500 flex items-center justify-center gap-2 hover:shadow-lg"
                            >
                              <Activity size={18} />
                              <span>Voir l'historique complet ({clubWars.length} guerres)</span>
                            </button>
                          )}
                        </>
                      ) : (
                        /* État vide */
                        <div className="text-center py-12">
                          <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-800 border-2 border-dashed border-slate-700 rounded-2xl mb-4">
                            <Swords size={40} className="text-slate-600" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2">Aucune guerre active</h3>
                          <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
                            Lancez un défi à un autre club pour commencer votre première guerre et gagner des points !
                          </p>
                          {canEdit() && (
                            <button
                              onClick={() => setShowWarModal(true)}
                              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-600 hover:from-indigo-700 hover:to-indigo-700 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-indigo-500/30 flex items-center justify-center gap-2 mx-auto"
                            >
                              <Target size={20} />
                              <span>Lancer un défi</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                    
                  </div>
                
              
            )}

            {/* Onglet 2: Feed */}
            {activeTab === 'feed' && (
              <div>
                
                <ClubFeed 
                  clubId={club._id}
                  isMember={isMember()} 
                  clubActivities={club.activities}  
                />
              </div>
            )}

            {/* Onglet 3: Calendrier */}
            {activeTab === 'calendar' && (
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <CalendarIcon size={20} className="text-indigo-500" />
                  Calendrier des séances
                </h3>
                <ClubCalendar club={club} />
              </div>
            )}

            {/* Onglet 4: Membres */}
            {activeTab === 'members' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                    <UsersIcon size={20} className="text-indigo-500" />
                    Membres ({nombreMembres})
                  </h3>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {membersToShow?.map((membre) => {
                    const membreData = typeof membre === 'object' ? membre : null;
                    if (!membreData) return null;

                    return (
                      <button
                        key={membreData._id}
                        onClick={() => navigate(`/user/${membreData._id}`)}
                        className="bg-slate-800 border border-slate-700 hover:border-indigo-500 rounded-xl p-3 transition-all hover:scale-105 active:scale-95"
                      >
                        <div className="flex flex-col items-center">
                          {membreData.photoProfil ? (
                            <img
                              src={membreData.photoProfil}
                              alt={`${membreData.prenom} ${membreData.nom}`}
                              className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-slate-600 mb-2"
                            />
                          ) : (
                            <img
                              src={defaultAvatar}
                              alt={`${membreData.prenom} ${membreData.nom}`}
                              className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-slate-600 mb-2"
                            />
                          )}
                          <p className="text-xs sm:text-sm font-semibold text-white truncate w-full text-center">
                            {membreData.prenom} {membreData.nom}
                          </p>
                          {club.createur && (
                            typeof club.createur === 'object' 
                              ? club.createur._id === membreData._id
                              : club.createur === membreData._id
                          ) && (
                            <span className="mt-1">
                              <Crown size={14} className="text-indigo-500" />
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {nombreMembres > 8 && (
                  <button
                    onClick={() => setShowAllMembers(!showAllMembers)}
                    className="mt-4 w-full py-2 bg-slate-800 hover:bg-slate-700 text-indigo-500 rounded-lg font-semibold transition flex items-center justify-center gap-2 border border-slate-700 hover:border-indigo-500"
                  >
                    {showAllMembers ? (
                      <>
                        <ChevronUp size={18} />
                        Voir moins
                      </>
                    ) : (
                      <>
                        <ChevronDown size={18} />
                        Voir tous les membres
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
</main>

      {/* Modal d'invitation */}
      {showInviteModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black bg-opacity-70"
            onClick={() => setShowInviteModal(false)}
          ></div>
          <div className="relative bg-slate-800 border border-indigo-500/50 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-4 sm:px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <UserPlus size={20} className="text-indigo-500" />
                Inviter des membres
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-300 p-2 hover:bg-slate-700 rounded-full transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(80vh-64px)]">
              {/* Barre de recherche */}
              <div className="mb-4">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      handleSearchUsers(e.target.value);
                    }}
                    placeholder="Rechercher un utilisateur..."
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Résultats de recherche */}
              {searchLoading ? (
                <div className="flex justify-center py-8">
                  <DotLoader size={30} color="#eab308" />
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-2">
                  {searchResults.map((searchUser) => (
                    <div
                      key={searchUser._id}
                      className="flex items-center justify-between p-3 bg-slate-900 border border-slate-700 hover:border-indigo-500/50 rounded-lg transition"
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {searchUser.photoProfil ? (
                          <img
                            src={searchUser.photoProfil}
                            alt={`${searchUser.prenom} ${searchUser.nom}`}
                            className="w-10 h-10 rounded-full object-cover border-2 border-slate-600"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center border-2 border-slate-600">
                            <span className="text-white font-bold text-sm">
                              {searchUser.prenom?.charAt(0)}{searchUser.nom?.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white truncate">
                            {searchUser.prenom} {searchUser.nom}
                          </p>
                          {searchUser.ville && (
                            <p className="text-sm text-gray-400 truncate">{searchUser.ville}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleInviteUser(searchUser._id)}
                        disabled={inviting}
                        className="ml-2 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-slate-900 rounded-lg font-semibold text-sm transition disabled:opacity-50 flex-shrink-0"
                      >
                        Inviter
                      </button>
                    </div>
                  ))}
                </div>
              ) : searchQuery.length >= 2 ? (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">Aucun utilisateur trouvé</p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Mail size={32} className="mx-auto mb-2 text-gray-600" />
                  <p className="text-sm">Recherchez un utilisateur pour l'inviter</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de création de guerre */}
      {showWarModal && (
        <CreateClubWar
          clubId={club._id}
          onClose={() => {
            setShowWarModal(false);
            fetchClubWars(); // Rafraîchir les guerres
          }}
        />
      )}

      {/* Modal Transfer Ownership */}
      {showTransferOwnershipModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black bg-opacity-70"
            onClick={() => setShowTransferOwnershipModal(false)}
          ></div>
          <div className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-white mb-4">
              Transférer la propriété du club
            </h3>
            <p className="text-gray-300 mb-4">
              Vous devez transférer la propriété avant de quitter le club.
            </p>
            
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {club.membres?.filter(m => m._id !== user._id).map((membre) => (
                <button
                  key={membre._id}
                  onClick={() => setSelectedNewOwner(membre._id)}
                  className={`w-full p-3 rounded-lg border-2 transition flex items-center gap-3 ${
                    selectedNewOwner === membre._id
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-slate-700 bg-slate-900 hover:border-slate-600'
                  }`}
                >
                  {membre.photoProfil ? (
                    <img
                      src={membre.photoProfil}
                      alt={`${membre.prenom} ${membre.nom}`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {membre.prenom?.charAt(0)}{membre.nom?.charAt(0)}
                      </span>
                    </div>
                  )}
                  <span className="text-white font-medium">
                    {membre.prenom} {membre.nom}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowTransferOwnershipModal(false)}
                className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition"
              >
                Annuler
              </button>
              <button
                onClick={handleTransferOwnership}
                disabled={!selectedNewOwner}
                className="flex-1 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg font-semibold transition"
              >
                Transférer et quitter
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <FooterPWA />
    </div>
  );
}

export default ClubPage;